import { useState } from "react";
import {
  Star,
  Quote,
  ArrowLeft,
  ArrowRight,
  MessageSquareQuote,
} from "lucide-react";

const testimonials = [
  {
    name: "Priya Mehta",
    initials: "PM",
    location: "Bangalore",
    project: "3BHK Home Interior",
    category: "Residential",
    content:
      "Cube4Spaces transformed our home into a space that feels beautiful and practical. The kitchen, wardrobes, lighting, and storage were planned thoughtfully. Every detail came together exactly the way we wanted.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=500&q=80",
  },
  {
    name: "Aditya Soman",
    initials: "AS",
    location: "Mumbai",
    project: "2BHK Apartment",
    category: "Residential",
    content:
      "We needed to make the most of a compact apartment. The team suggested smart layouts, clean finishes, and practical storage solutions that made our home feel more spacious and comfortable.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=500&q=80",
  },
  {
    name: "Neha Reddy",
    initials: "NR",
    location: "Hyderabad",
    project: "Modern Office Design",
    category: "Commercial",
    content:
      "Our new office needed to look professional while remaining comfortable for the team. The space planning, lighting, and finishes created a balanced working environment that reflects our brand.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=500&q=80",
  },
  {
    name: "Rajesh Iyer",
    initials: "RI",
    location: "Pune",
    project: "Turnkey Home Project",
    category: "Turnkey",
    content:
      "Having one team coordinate the entire project made the process easier to manage. From planning and material selection to finishing touches, communication and coordination were important throughout.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=500&q=80",
  },
  {
    name: "Sneha Kapoor",
    initials: "SK",
    location: "Bangalore",
    project: "Modular Kitchen",
    category: "Kitchen",
    content:
      "Our kitchen now has a much better layout, useful storage, and a clean, contemporary look. The material suggestions helped us balance the appearance of the kitchen with everyday usability.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=500&q=80",
  },
  {
    name: "Karthik Nair",
    initials: "KN",
    location: "Chennai",
    project: "Retail Interior",
    category: "Commercial",
    content:
      "The retail space needed to showcase our products clearly and create a welcoming customer experience. The display planning, lighting, and overall layout helped bring our vision together.",
    rating: 5,
    image:
      "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=500&q=80",
  },
];

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);

  const active = testimonials[activeIndex];

  const showPrevious = () => {
    setActiveIndex((current) =>
      current === 0 ? testimonials.length - 1 : current - 1
    );
  };

  const showNext = () => {
    setActiveIndex((current) =>
      current === testimonials.length - 1 ? 0 : current + 1
    );
  };

  return (
    <section className="relative w-full overflow-hidden bg-white py-14 sm:py-16 md:py-20 lg:py-28">
      {/* Background decoration */}
      <div className="pointer-events-none absolute -right-32 top-0 h-64 w-64 rounded-full bg-indigo-100/60 blur-3xl sm:h-80 sm:w-80 lg:h-[28rem] lg:w-[28rem]" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 h-64 w-64 rounded-full bg-slate-100 blur-3xl sm:h-80 sm:w-80" />

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section heading */}
        <div className="mb-10 grid gap-6 sm:mb-12 md:mb-16 lg:grid-cols-[1fr_0.7fr] lg:items-end lg:gap-12">
          <div>
            <div className="mb-5 inline-flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-4 py-2">
              <MessageSquareQuote
                size={15}
                className="text-indigo-600"
              />
              <span className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[0.18em] text-slate-600 sm:text-xs">
                Client Experiences
              </span>
            </div>

            <h2 className="max-w-3xl font-['Poppins',sans-serif] text-3xl font-semibold leading-[1.2] tracking-tight text-slate-950 sm:text-4xl md:text-5xl lg:text-6xl">
              Spaces that speak.
              <span className="mt-1 block text-indigo-600">
                Stories that matter.
              </span>
            </h2>
          </div>

          <div className="flex flex-col justify-between gap-5 lg:pb-2">
            <p className="max-w-xl font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-600 sm:text-base md:text-lg">
              Every space has a story behind it. Explore experiences from
              clients who turned their ideas into thoughtfully designed living
              and working spaces.
            </p>

            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {testimonials.slice(0, 4).map((item) => (
                  <img
                    key={item.name}
                    src={item.image}
                    alt=""
                    className="h-9 w-9 rounded-full border-2 border-white object-cover sm:h-10 sm:w-10"
                    loading="lazy"
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, index) => (
                    <Star
                      key={index}
                      size={12}
                      className="fill-amber-400 text-amber-400"
                    />
                  ))}
                </div>
                <p className="mt-1 font-['DM_Sans',sans-serif] text-xs text-slate-500 sm:text-sm">
                  Thoughtful spaces, personal stories
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main review panel */}
        <div className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr] lg:gap-7 xl:gap-10">
          {/* Client selection panel */}
          <div className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 sm:rounded-3xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 sm:px-6 sm:py-5">
              <div>
                <p className="font-['Poppins',sans-serif] text-sm font-semibold text-slate-900 sm:text-base">
                  Client stories
                </p>
                <p className="mt-1 font-['DM_Sans',sans-serif] text-xs text-slate-500 sm:text-sm">
                  Select a review to explore
                </p>
              </div>

              <span className="font-['DM_Sans',sans-serif] text-xs font-semibold tabular-nums text-slate-400 sm:text-sm">
                {String(activeIndex + 1).padStart(2, "0")} /{" "}
                {String(testimonials.length).padStart(2, "0")}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 lg:grid-cols-2 lg:gap-0 lg:p-2 xl:grid-cols-2">
              {testimonials.map((testimonial, index) => {
                const isActive = activeIndex === index;

                return (
                  <button
                    key={testimonial.name}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-pressed={isActive}
                    className={`group flex min-w-0 items-center gap-2.5 rounded-xl border p-2.5 text-left transition-all duration-300 sm:gap-3 sm:p-3 ${
                      isActive
                        ? "border-indigo-200 bg-white shadow-sm"
                        : "border-transparent bg-transparent hover:border-slate-200 hover:bg-white"
                    }`}
                  >
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      loading="lazy"
                      className={`h-10 w-10 shrink-0 rounded-lg object-cover transition-all sm:h-12 sm:w-12 sm:rounded-xl ${
                        isActive ? "" : "grayscale-[35%] group-hover:grayscale-0"
                      }`}
                    />

                    <span className="min-w-0 flex-1">
                      <span
                        className={`block truncate font-['Poppins',sans-serif] text-xs font-semibold sm:text-sm ${
                          isActive ? "text-indigo-700" : "text-slate-800"
                        }`}
                      >
                        {testimonial.name}
                      </span>
                      <span className="mt-1 block truncate font-['DM_Sans',sans-serif] text-[10px] text-slate-500 sm:text-xs">
                        {testimonial.category}
                      </span>
                    </span>

                    <span
                      className={`hidden h-2 w-2 shrink-0 rounded-full sm:block ${
                        isActive ? "bg-indigo-600" : "bg-slate-300"
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            <div className="mt-auto border-t border-slate-200 px-5 py-4 sm:px-6">
              <p className="font-['DM_Sans',sans-serif] text-xs leading-5 text-slate-500 sm:text-sm">
                Different spaces. Different needs. One focus on thoughtful
                design and execution.
              </p>
            </div>
          </div>

          {/* Featured testimonial */}
          <article
            key={active.name}
            className="relative flex min-h-[390px] flex-col overflow-hidden rounded-2xl bg-slate-950 p-6 text-white sm:min-h-[430px] sm:rounded-3xl sm:p-8 md:p-10 lg:min-h-[500px] lg:p-12 xl:p-14"
          >
            {/* Decorative quote */}
            <Quote
              aria-hidden="true"
              className="pointer-events-none absolute -right-3 -top-4 h-28 w-28 rotate-180 text-white/[0.06] sm:-right-4 sm:-top-5 sm:h-40 sm:w-40 lg:h-52 lg:w-52"
              strokeWidth={1}
            />

            <div className="relative z-10 flex flex-1 flex-col">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <span className="inline-flex items-center rounded-full border border-white/15 bg-white/[0.06] px-3 py-1.5 font-['DM_Sans',sans-serif] text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-200 sm:text-xs">
                  {active.category} Project
                </span>

                <div
                  className="flex items-center gap-1"
                  aria-label={`${active.rating} out of 5 stars`}
                >
                  {[...Array(active.rating)].map((_, index) => (
                    <Star
                      key={index}
                      size={15}
                      className="fill-amber-400 text-amber-400 sm:h-4 sm:w-4"
                    />
                  ))}
                </div>
              </div>

              <div className="my-8 flex-1 sm:my-10 lg:my-12">
                <p className="mb-4 font-['DM_Sans',sans-serif] text-xs font-semibold uppercase tracking-[0.18em] text-indigo-300 sm:text-sm">
                  A client perspective
                </p>

                <blockquote className="max-w-3xl font-['Poppins',sans-serif] text-xl font-medium leading-[1.55] tracking-tight text-white sm:text-2xl md:text-3xl lg:text-4xl lg:leading-[1.45]">
                  “{active.content}”
                </blockquote>

                <p className="mt-5 max-w-2xl font-['DM_Sans',sans-serif] text-sm leading-7 text-slate-400 sm:mt-6 sm:text-base">
                  A well-designed space is about more than appearance. It is
                  about how comfortably it works for the people who use it.
                </p>
              </div>

              <div className="flex flex-col gap-5 border-t border-white/15 pt-5 sm:flex-row sm:items-center sm:justify-between sm:pt-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <img
                    src={active.image}
                    alt={active.name}
                    className="h-12 w-12 shrink-0 rounded-full border border-white/20 object-cover sm:h-14 sm:w-14"
                  />

                  <div className="min-w-0">
                    <h3 className="truncate font-['Poppins',sans-serif] text-sm font-semibold text-white sm:text-base">
                      {active.name}
                    </h3>
                    <p className="mt-1 font-['DM_Sans',sans-serif] text-xs text-slate-400 sm:text-sm">
                      {active.project}
                    </p>
                    <p className="mt-0.5 font-['DM_Sans',sans-serif] text-xs text-slate-500">
                      {active.location}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <button
                    type="button"
                    onClick={showPrevious}
                    aria-label="Previous testimonial"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-white transition hover:border-white hover:bg-white hover:text-slate-950 sm:h-11 sm:w-11"
                  >
                    <ArrowLeft size={17} />
                  </button>

                  <button
                    type="button"
                    onClick={showNext}
                    aria-label="Next testimonial"
                    className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-500 text-white transition hover:bg-indigo-400 sm:h-11 sm:w-11"
                  >
                    <ArrowRight size={17} />
                  </button>
                </div>
              </div>
            </div>
          </article>
        </div>

        {/* Bottom statement */}
        <div className="mt-10 flex flex-col gap-3 border-t border-slate-200 pt-6 sm:mt-12 sm:flex-row sm:items-center sm:justify-between md:mt-16">
          <p className="font-['Poppins',sans-serif] text-sm font-medium text-slate-800 sm:text-base">
            Your space. Your vision. Thoughtfully brought to life.
          </p>
          <p className="font-['DM_Sans',sans-serif] text-xs leading-5 text-slate-500 sm:text-sm">
            Cube4Spaces & Paint Studio
          </p>
        </div>
      </div>
    </section>
  );
}